import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Card, 
  Table, 
  Button, 
  Form, 
  Row, 
  Col, 
  Alert, 
  Badge, 
  Dropdown,
  Modal,
  Spinner,
  InputGroup,
  Accordion
} from 'react-bootstrap';
import { Patient, getAllPatients, deletePatient } from '../../api/admin';
import { 
  genderOptions, 
  familyStatusOptions, 
  educationLevelOptions, 
  supportNetworkOptions, 
  financialStatusOptions
} from '../../components/form/FormOptions';
import { QuestionnaireAssignmentModal } from '../../components/questionnaire/QuestionnaireAssignmentModal';
import './PatientList.css';

interface FilterState {
  search: string;
  gender: string;
  familyStatus: string;
  educationLevel: string;
  ageRange: string;
  supportNetwork: string;
  financialStatus: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface PaginationState {
  currentPage: number;
  patientsPerPage: number;
  totalDisplayed: number;
}

export function PatientList() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [displayedPatients, setDisplayedPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState<Patient | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [showQuestionnaireModal, setShowQuestionnaireModal] = useState(false);
  const [selectedPatientIdForQuestionnaire, setSelectedPatientIdForQuestionnaire] = useState<string>('');
  const [filtersOpen, setFiltersOpen] = useState<string | null>(() => {
    // Start closed on mobile, open on desktop
    return window.innerWidth <= 768 ? null : '0';
  });
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    gender: '',
    familyStatus: '',
    educationLevel: '',
    ageRange: '',
    supportNetwork: '',
    financialStatus: '',
    sortBy: '',
    sortOrder: 'asc'
  });
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    patientsPerPage: 15,
    totalDisplayed: 15
  });

  // Filter options
  const ageRangeOptions = ['0-18', '19-30', '31-45', '46-60', '61+'];
  const patientsPerPageOptions = [15, 25, 50, 100];
  const sortOptions = [
    { value: '', label: 'Default Order' },
    { value: 'serial_number', label: 'Serial Number' },
    { value: 'name', label: 'Name (A-Z)' },
    { value: 'age', label: 'Age (Youngest First)' },
    { value: 'gender', label: 'Gender' },
    { value: 'family_status', label: 'Family Status' },
    { value: 'education_level', label: 'Education Level' }
  ];

  // Load patients on component mount
  useEffect(() => {
    loadPatients();
  }, []);

  const calculateAge = (dateOfBirth: string): number => {
    const birth = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getAgeRange = useCallback((dateOfBirth: string): string => {
    const age = calculateAge(dateOfBirth);
    if (age <= 18) return '0-18';
    if (age <= 30) return '19-30';
    if (age <= 45) return '31-45';
    if (age <= 60) return '46-60';
    return '61+';
  }, []);

  const applyFilters = useCallback(() => {
    let filtered = [...patients];

    // Search filter (name)
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(patient => 
        patient.last_name.toLowerCase().includes(searchTerm) ||
        patient.first_name.toLowerCase().includes(searchTerm)
      );
    }

    // Gender filter
    if (filters.gender) {
      filtered = filtered.filter(patient => patient.gender === filters.gender);
    }

    // Family status filter
    if (filters.familyStatus) {
      filtered = filtered.filter(patient => patient.family_status === filters.familyStatus);
    }

    // Education level filter
    if (filters.educationLevel) {
      filtered = filtered.filter(patient => patient.education_level === filters.educationLevel);
    }

    // Age range filter
    if (filters.ageRange && filters.ageRange !== '') {
      filtered = filtered.filter(patient => {
        if (!patient.date_of_birth) return false;
        return getAgeRange(patient.date_of_birth) === filters.ageRange;
      });
    }

    // Support network filter
    if (filters.supportNetwork) {
      filtered = filtered.filter(patient => patient.support_network === filters.supportNetwork);
    }

    // Financial status filter
    if (filters.financialStatus) {
      filtered = filtered.filter(patient => patient.financial_status === filters.financialStatus);
    }

    // Apply sorting
    if (filters.sortBy) {
      filtered.sort((a, b) => {
        let comparison = 0;
        
        if (filters.sortBy === 'serial_number') {
          comparison = (a.serial_number || 0) - (b.serial_number || 0);
        } else if (filters.sortBy === 'name') {
          const nameA = `${a.last_name} ${a.first_name}`.toLowerCase();
          const nameB = `${b.last_name} ${b.first_name}`.toLowerCase();
          comparison = nameA.localeCompare(nameB);
        } else if (filters.sortBy === 'age') {
          const ageA = a.date_of_birth ? calculateAge(a.date_of_birth) : 0;
          const ageB = b.date_of_birth ? calculateAge(b.date_of_birth) : 0;
          comparison = ageA - ageB;
        } else if (filters.sortBy === 'gender') {
          const genderA = a.gender || '';
          const genderB = b.gender || '';
          comparison = genderA.localeCompare(genderB);
        } else if (filters.sortBy === 'family_status') {
          const statusA = a.family_status || '';
          const statusB = b.family_status || '';
          comparison = statusA.localeCompare(statusB);
        } else if (filters.sortBy === 'education_level') {
          const eduA = a.education_level || '';
          const eduB = b.education_level || '';
          comparison = eduA.localeCompare(eduB);
        }
        
        return filters.sortOrder === 'desc' ? -comparison : comparison;
      });
    }

    setFilteredPatients(filtered);
  }, [patients, filters, getAgeRange]);

  const updateDisplayedPatients = useCallback(() => {
    const startIndex = 0;
    const endIndex = pagination.totalDisplayed;
    const patientsToShow = filteredPatients.slice(startIndex, endIndex);
    setDisplayedPatients(patientsToShow);
  }, [filteredPatients, pagination.totalDisplayed]);

  // Apply filters when patients or filters change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Update displayed patients when filtered patients or pagination changes
  useEffect(() => {
    updateDisplayedPatients();
  }, [updateDisplayedPatients]);

  // Handle screen resize for accordion behavior
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      // Only close if currently open and switching to mobile
      if (isMobile && filtersOpen === '0') {
        setFiltersOpen(null);
      }
      // Only open if currently closed and switching to desktop
      else if (!isMobile && filtersOpen === null) {
        setFiltersOpen('0');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [filtersOpen]);

  // Handle scroll for floating button visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const isMobile = window.innerWidth <= 768;
      
      // Show floating button on mobile when scrolled down 200px
      if (isMobile) {
        setShowFloatingButton(scrollTop > 200);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadPatients = async () => {
    try {
      setLoading(true);
      setError('');
      const fetchedPatients = await getAllPatients();
      setPatients(fetchedPatients);
    } catch (err: any) {
      setError('Failed to load patients. Please try again.');
      console.error('Error loading patients:', err);
    } finally {
      setLoading(false);
    }
  };



  const handleFilterChange = (filterName: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    // Reset pagination when filters change
    setPagination(prev => ({ ...prev, currentPage: 1, totalDisplayed: prev.patientsPerPage }));
  };

  const handlePatientsPerPageChange = (newPatientsPerPage: number) => {
    setPagination(prev => ({
      ...prev,
      patientsPerPage: newPatientsPerPage,
      currentPage: 1,
      totalDisplayed: newPatientsPerPage
    }));
  };

  const handleShowMore = () => {
    setPagination(prev => ({
      ...prev,
      totalDisplayed: Math.min(prev.totalDisplayed + prev.patientsPerPage, filteredPatients.length)
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      gender: '',
      familyStatus: '',
      educationLevel: '',
      ageRange: '',
      supportNetwork: '',
      financialStatus: '',
      sortBy: '',
      sortOrder: 'asc'
    });
    // Reset pagination when clearing filters
    setPagination(prev => ({ ...prev, currentPage: 1, totalDisplayed: prev.patientsPerPage }));
  };

  const handleSortChange = (sortBy: string) => {
    setFilters(prev => {
      // If clicking the same sort field, toggle the order
      if (prev.sortBy === sortBy) {
        return { ...prev, sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' };
      }
      // If clicking a new sort field, set it with ascending order
      return { ...prev, sortBy, sortOrder: 'asc' };
    });
  };

  const handleDeleteClick = (patient: Patient) => {
    setPatientToDelete(patient);
    setDeleteSuccess(false);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!patientToDelete) return;
    
    try {
      setIsDeleting(true);
      await deletePatient(patientToDelete.patient_id);
      
      // Remove patient from local state
      setPatients(prev => prev.filter(p => p.patient_id !== patientToDelete.patient_id));
      
      // Show success state in modal
      setDeleteSuccess(true);
      
      // Close modal after showing success for 2 seconds
      setTimeout(() => {
        setShowDeleteModal(false);
        // Reset states after modal close animation completes
        setTimeout(() => {
          setPatientToDelete(null);
          setDeleteSuccess(false);
        }, 300); // Bootstrap modal fade animation takes ~300ms
      }, 2000);
      
    } catch (error: any) {
      console.error('Error deleting patient:', error);
      setError(`Failed to delete patient: ${error.message || 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleView = (patientId: string) => {
    navigate(`/admin/patients/view/${patientId}`);
  };

  const handleAssignQuestionnaire = (patientId: string) => {
    setSelectedPatientIdForQuestionnaire(patientId);
    setShowQuestionnaireModal(true);
  };

  const handleRowHover = (patientId: string) => {
    // Close any open dropdown when hovering over a different patient
    if (openDropdownId && openDropdownId !== patientId) {
      setOpenDropdownId(null);
    }
  };

  const handleDropdownToggle = (patientId: string, isOpen: boolean) => {
    setOpenDropdownId(isOpen ? patientId : null);
  };

  const formatLabel = (value: string): string => {
    return value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Helper function to format phone number with country code
  const formatPhoneNumber = (phone: string, countryCode: string): string => {
    if (!phone) return 'N/A';
    const cleanPhone = phone.replace(/\D/g, '');
    const formattedPhone = cleanPhone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    
    // Ensure country code has only one '+' symbol
    const cleanCountryCode = countryCode ? countryCode.replace(/^\+*/, '+') : '';
    return cleanCountryCode ? `${cleanCountryCode} ${formattedPhone}` : formattedPhone;
  };

  return (
    <Container 
      fluid 
      className="patient-list-container admin-background"
      style={{
        backgroundImage: 'url(/Login.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      <Card className="patient-card">
        <Card.Header className="patient-header">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0">
              <i className="bi bi-people-fill me-2"></i>
              Patient Management
            </h2>
            <div className="d-flex gap-2">
              <Button
                variant="light"
                onClick={() => navigate('/admin/patients/new')}
                className="d-flex align-items-center"
              >
                <i className="bi bi-person-plus me-2"></i>
                Add New Patient
              </Button>
              <Button
                variant="outline-light"
                onClick={() => navigate('/')}
                className="d-flex align-items-center"
              >
                <i className="bi bi-house me-2"></i>
                Home
              </Button>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="p-4">
          {/* Filters Section */}
          <Accordion 
            activeKey={filtersOpen} 
            onSelect={(eventKey) => setFiltersOpen(eventKey as string | null)}
            className="mb-4"
          >
            <Accordion.Item eventKey="1" className="filters-card">
              <Accordion.Header>
                <div className="d-flex align-items-center justify-content-between w-100 me-3">
                  <div>
                    <i className="bi bi-funnel me-2"></i>
                    <strong>Filters & Search</strong>
                  </div>
                  <div className="d-flex align-items-center">
                    {(filters.search || filters.gender || filters.familyStatus || 
                      filters.educationLevel || filters.ageRange || filters.supportNetwork || 
                      filters.financialStatus || filters.sortBy) && (
                      <Badge bg="primary" className="me-2">
                        Active Filters
                      </Badge>
                    )}
                    <small className="text-muted">
                      {filtersOpen ? 'Click to collapse' : 'Click to expand filters'}
                    </small>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body>
              <Row>

                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Gender</Form.Label>
                    <Form.Select
                      value={filters.gender}
                      onChange={(e) => handleFilterChange('gender', e.target.value)}
                    >
                      <option value="">All Genders</option>
                      {genderOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Age Range</Form.Label>
                    <Form.Select
                      value={filters.ageRange}
                      onChange={(e) => handleFilterChange('ageRange', e.target.value)}
                    >
                      <option value="">All Ages</option>
                      {ageRangeOptions.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Family Status</Form.Label>
                    <Form.Select
                      value={filters.familyStatus}
                      onChange={(e) => handleFilterChange('familyStatus', e.target.value)}
                    >
                      <option value="">All Statuses</option>
                      {familyStatusOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Education</Form.Label>
                    <Form.Select
                      value={filters.educationLevel}
                      onChange={(e) => handleFilterChange('educationLevel', e.target.value)}
                    >
                      <option value="">All Levels</option>
                      {educationLevelOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Support Network</Form.Label>
                    <Form.Select
                      value={filters.supportNetwork}
                      onChange={(e) => handleFilterChange('supportNetwork', e.target.value)}
                    >
                      <option value="">All Networks</option>
                      {supportNetworkOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Financial Status</Form.Label>
                    <Form.Select
                      value={filters.financialStatus}
                      onChange={(e) => handleFilterChange('financialStatus', e.target.value)}
                    >
                      <option value="">All Statuses</option>
                      {financialStatusOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sort By</Form.Label>
                    <Form.Select
                      value={filters.sortBy}
                      onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    >
                      {sortOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <div className="d-flex gap-2 mb-3 w-100">
                    {filters.sortBy && (
                      <Button 
                        variant="outline-primary" 
                        onClick={() => handleFilterChange('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc')}
                        className="flex-grow-1"
                      >
                        <i className={`bi bi-sort-${
                          filters.sortBy === 'name' || filters.sortBy === 'gender' || filters.sortBy === 'family_status' || filters.sortBy === 'education_level' 
                            ? 'alpha' 
                            : 'numeric'
                        }-${filters.sortOrder === 'asc' ? 'down' : 'up'} me-2`}></i>
                        {filters.sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
                      </Button>
                    )}
                    <Button variant="outline-secondary" onClick={clearFilters}>
                      <i className="bi bi-arrow-clockwise me-2"></i>
                      Clear All
                    </Button>
                  </div>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

          {/* Quick Filters Summary (when accordion is collapsed) */}
          {!filtersOpen && (filters.search || filters.gender || filters.familyStatus || 
            filters.educationLevel || filters.ageRange || filters.supportNetwork || 
            filters.financialStatus || filters.sortBy) && (
            <div className="quick-filters-summary mb-3 p-3 bg-light rounded">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <small className="text-muted fw-bold">
                  <i className="bi bi-funnel-fill me-1"></i>
                  Active Filters:
                </small>
                <Button 
                  variant="link" 
                  size="sm" 
                  onClick={clearFilters}
                  className="p-0 text-decoration-none"
                >
                  <i className="bi bi-x-circle"></i> Clear All
                </Button>
              </div>
              <div className="d-flex flex-wrap gap-1">
                {filters.search && (
                  <Badge bg="primary" className="filter-tag">
                    Search: "{filters.search}"
                  </Badge>
                )}
                {filters.gender && (
                  <Badge bg="secondary" className="filter-tag">
                    Gender: {formatLabel(filters.gender)}
                  </Badge>
                )}
                {filters.ageRange && (
                  <Badge bg="info" className="filter-tag">
                    Age: {filters.ageRange}
                  </Badge>
                )}
                {filters.familyStatus && (
                  <Badge bg="success" className="filter-tag">
                    Status: {formatLabel(filters.familyStatus)}
                  </Badge>
                )}
                {filters.educationLevel && (
                  <Badge bg="warning" className="filter-tag text-dark">
                    Education: {formatLabel(filters.educationLevel)}
                  </Badge>
                )}
                {filters.supportNetwork && (
                  <Badge bg="dark" className="filter-tag">
                    Support: {formatLabel(filters.supportNetwork)}
                  </Badge>
                )}
                {filters.financialStatus && (
                  <Badge bg="danger" className="filter-tag">
                    Financial: {formatLabel(filters.financialStatus)}
                  </Badge>
                )}
                {filters.sortBy && (
                  <Badge bg="light" className="filter-tag text-dark border">
                    Sort: {formatLabel(filters.sortBy)} ({filters.sortOrder === 'asc' ? 'A-Z' : 'Z-A'})
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Results Section */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2">
              <h5 className="mb-0">
                Showing {displayedPatients.length} of {filteredPatients.length} patients
              </h5>
              <div className="d-flex align-items-center justify-content-between w-100 w-md-auto gap-2">
                <div className="d-flex align-items-center gap-2">
                  <small className="text-muted">Show:</small>
                  <Form.Select
                    size="sm"
                    value={pagination.patientsPerPage}
                    onChange={(e) => handlePatientsPerPageChange(Number(e.target.value))}
                    style={{ width: 'auto', minWidth: '120px' }}
                  >
                    {patientsPerPageOptions.map(option => (
                      <option key={option} value={option}>
                        {option} per page
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <Badge bg="info" className="fs-6 d-block d-md-none">
                  {filteredPatients.length} Total Results
                </Badge>
              </div>
            </div>
            <Badge bg="info" className="fs-6 d-none d-md-block">
              {filteredPatients.length} Total Results
            </Badge>
          </div>

          {/* Error Display */}
          {error && (
            <Alert variant="danger" className="mb-4">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading patients...</p>
            </div>
          ) : (
            /* Patients Table */
            <Table responsive hover className="shadow-sm">
              <thead className="table-dark">
                <tr>
                  <th 
                    className="sortable-header" 
                    onClick={() => handleSortChange('serial_number')}
                    style={{ cursor: 'pointer', width: '100px' }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      S/N
                      {filters.sortBy === 'serial_number' && (
                        <i className={`bi bi-sort-numeric-${filters.sortOrder === 'asc' ? 'down' : 'up'}`}></i>
                      )}
                    </div>
                  </th>
                  <th className="name-column-header">
                    <div className="d-flex align-items-center">
                      <span 
                        className="sortable-label me-3" 
                        onClick={() => handleSortChange('name')}
                        style={{ cursor: 'pointer', minWidth: 'fit-content', lineHeight: '1' }}
                      >
                        Name
                        {filters.sortBy === 'name' && (
                          <i className={`bi bi-sort-alpha-${filters.sortOrder === 'asc' ? 'down' : 'up'} ms-1`}></i>
                        )}
                      </span>
                      <div className="flex-grow-1 d-flex align-items-center" style={{ maxWidth: '160px' }}>
                        <InputGroup size="sm" className="mb-0">
                          <Form.Control
                            type="text"
                            placeholder="Search..."
                            value={filters.search}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleFilterChange('search', e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.375rem', height: '28px' }}
                          />
                          {filters.search && (
                            <Button 
                              variant="outline-secondary" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFilterChange('search', '');
                              }}
                              style={{ padding: '0.125rem 0.375rem', fontSize: '0.65rem', height: '28px' }}
                            >
                              <i className="bi bi-x"></i>
                            </Button>
                          )}
                        </InputGroup>
                      </div>
                    </div>
                  </th>
                  <th 
                    className="sortable-header" 
                    onClick={() => handleSortChange('age')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      Age
                      {filters.sortBy === 'age' && (
                        <i className={`bi bi-sort-numeric-${filters.sortOrder === 'asc' ? 'down' : 'up'}`}></i>
                      )}
                    </div>
                  </th>
                  <th>AMKA</th>
                  <th>Mobile Phone</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedPatients.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4">
                      <i className="bi bi-inbox display-4 text-muted"></i>
                      <p className="text-muted mt-2">No patients found matching your criteria.</p>
                    </td>
                  </tr>
                ) : (
                  displayedPatients.map((patient) => (
                    <tr key={patient.patient_id} onMouseEnter={() => handleRowHover(patient.patient_id)}>
                      <td className="text-center">
                        <Badge pill bg="secondary" >
                          #{patient.serial_number}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div>
                            <div className="fw-bold">{patient.last_name} {patient.first_name}</div>
                            <small className="text-muted d-none d-md-inline">{patient.email || 'N/A'}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        {patient.date_of_birth ? (
                          <div>
                            <div>{calculateAge(patient.date_of_birth)} years</div>
                            <small className="text-muted d-none d-md-block">
                              {new Date(patient.date_of_birth).toLocaleDateString()}
                            </small>
                          </div>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                      <td>
                        <code className="bg-light px-2 py-1 rounded text-dark fw-bold">
                          {patient.amka || 'N/A'}
                        </code>
                      </td>
                      <td>
                        {patient.mobile_phone ? (
                          <a 
                            href={`tel:${patient.mobile_country_code || ''}${patient.mobile_phone}`}
                            className="text-decoration-none d-flex align-items-center"
                            title="Click to call"
                          >
                            <i className="bi bi-telephone me-2 text-success"></i>
                            <span>
                              {formatPhoneNumber(patient.mobile_phone, patient.mobile_country_code || '')}
                            </span>
                          </a>
                        ) : (
                          <span className="text-muted d-flex align-items-center">
                            <i className="bi bi-telephone-x me-2"></i>
                            No phone
                          </span>
                        )}
                      </td>
                      <td>
                        <Dropdown
                          show={openDropdownId === patient.patient_id}
                          onToggle={(isOpen) => handleDropdownToggle(patient.patient_id, isOpen)}
                          drop="down"
                        >
                          <Dropdown.Toggle 
                            variant="outline-primary" 
                            size="sm"
                            className="actions-btn"
                          >
                            <i className="bi bi-three-dots-vertical"></i>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleView(patient.patient_id)}>
                              <i className="bi bi-eye me-2"></i>
                              View Details
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleAssignQuestionnaire(patient.patient_id)}>
                              <i className="bi bi-clipboard-check me-2"></i>
                              Assign Questionnaire
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item 
                              onClick={() => handleDeleteClick(patient)}
                              className="text-danger"
                            >
                              <i className="bi bi-trash me-2"></i>
                              Delete Patient
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}

          {/* Show More Button */}
          {displayedPatients.length < filteredPatients.length && (
            <div className="text-center mt-4">
              <Button 
                variant="outline-primary" 
                onClick={handleShowMore}
                className="px-4 py-2"
              >
                <i className="bi bi-arrow-down-circle me-2"></i>
                Show More Results ({Math.min(pagination.patientsPerPage, filteredPatients.length - displayedPatients.length)} more)
              </Button>
              <div className="mt-2">
                <small className="text-muted">
                  Showing {displayedPatients.length} of {filteredPatients.length} patients
                </small>
              </div>
            </div>
          )}

          {/* Show All Results Button (when there are many hidden results) */}
          {filteredPatients.length - displayedPatients.length > pagination.patientsPerPage && (
            <div className="text-center mt-2">
              <Button 
                variant="link" 
                size="sm"
                onClick={() => setPagination(prev => ({ ...prev, totalDisplayed: filteredPatients.length }))}
                className="text-decoration-none"
              >
                <i className="bi bi-list-ul me-1"></i>
                Show All {filteredPatients.length} Results
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal 
        show={showDeleteModal} 
        onHide={() => !isDeleting && setShowDeleteModal(false)} 
        centered
        backdrop={isDeleting ? 'static' : true}
        keyboard={!isDeleting}
      >
        <Modal.Header closeButton={!isDeleting && !deleteSuccess}>
          <Modal.Title>
            {deleteSuccess ? 'Patient Deleted' : 'Confirm Delete'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {patientToDelete && (
            <>
              {deleteSuccess ? (
                <Alert variant="success">
                  <i className="bi bi-check-circle me-2"></i>
                  Patient <strong>{patientToDelete.first_name} {patientToDelete.last_name}</strong> has been deleted successfully.
                </Alert>
              ) : (
                <>
                  <Alert variant="warning">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Are you sure you want to delete patient <strong>{patientToDelete.first_name} {patientToDelete.last_name}</strong>?
                  </Alert>
                  <div className="mb-3">
                    <p className="text-muted mb-2">
                      <strong>⚠️ This action will permanently delete:</strong>
                    </p>
                    <ul className="text-muted small mb-0">
                      <li>Patient record and personal information</li>
                      <li>All questionnaire responses and results</li>
                      <li>Registration tokens and session data</li>
                      <li>All associated medical data and history</li>
                    </ul>
                  </div>
                  <Alert variant="danger" className="mb-0">
                    <i className="bi bi-shield-exclamation me-2"></i>
                    <strong>The comprehensive deletion cannot be undone.</strong>
                  </Alert>
                </>
              )}
            </>
          )}
        </Modal.Body>
        {!deleteSuccess && (
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              style={{ minWidth: '140px' }}
            >
              {isDeleting ? (
                <>
                  <i className="bi bi-trash me-2"></i>
                  <span>Deleting</span>
                  <span className="loading-dots"></span>
                </>
              ) : (
                <>
                  <i className="bi bi-trash me-2"></i>
                  Delete Patient
                </>
              )}
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Questionnaire Assignment Modal */}
      <QuestionnaireAssignmentModal
        show={showQuestionnaireModal}
        onHide={() => {
          setShowQuestionnaireModal(false);
          setSelectedPatientIdForQuestionnaire('');
        }}
        preselectedPatientId={selectedPatientIdForQuestionnaire}
      />

      {/* Floating Quick Add Button for Mobile */}
      <Button 
        className={`floating-add-button ${!showFloatingButton ? 'hidden' : ''}`}
        onClick={() => navigate('/admin/patients/new')}
        title="Add New Patient"
      >
        <i className="bi bi-plus-lg"></i>
      </Button>
    </Container>
  );
}